import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { GetProductsDto } from '@/types/dtos/orders/response/get-products.dto.ts';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto.ts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export const GET_PRODUCTS_QUERY_KEY = 'get-products';

const SCOPE_TYPE_NORMALIZE: Record<string, ProductDto['scopeType']> = {
  event: 'Event',
  organization: 'Organization',
  flow: 'Flow',
  venue: 'Venue',
};

function normalizeProductFromApi(product: ProductDto): ProductDto {
  const kind = typeof product.kind === 'string' ? (product.kind.toUpperCase() as ProductDto['kind']) : product.kind;
  const scopeRaw = product.scopeType;
  const scopeType =
    typeof scopeRaw === 'string' ? (SCOPE_TYPE_NORMALIZE[scopeRaw.trim().toLowerCase()] ?? scopeRaw) : scopeRaw;
  return { ...product, kind, scopeType };
}

export const useGetProductsQuery = () => {
  const { getToken } = useAuth();
  const { hasAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  const query = useQuery({
    enabled: hasAccess,
    queryKey: [GET_PRODUCTS_QUERY_KEY],
    queryFn: async () => {
      const token = await getToken();
      const products = await getProducts(token ?? '');
      return products.map((product) => normalizeProductFromApi(product));
    },
  });

  // Mask cached catalog data the moment access is revoked, even before the query key is evicted.
  if (!hasAccess) {
    return { ...query, data: undefined } as typeof query;
  }
  return query;
};

const getProducts = async (token: string): Promise<GetProductsDto> => {
  const url = `${import.meta.env.VITE_API_URL}/products`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
